import { Component, OnInit, signal, effect } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RouterOutlet } from '@angular/router';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { IframeSecurityService } from 'app/services/iframeSecurity.service';
import { TranslocoService } from '@jsverse/transloco';
import { FerrerAuthService } from './services/authentication.service';
import { DialogService } from './services/dialog.service';
import { AuthCheckService } from './modules/utils/auth-check.service';
import { SessionService } from './services/session.service';
import { OtpDialogComponent } from './modules/dialogs/otp-dialog/otp-dialog.component';
import { RegisterFlowComponent } from './modules/register-flow/register-flow.component';
import { NavigationFlowService } from './services/navigation-flow.service';
import { Subscription, firstValueFrom } from 'rxjs';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [RouterOutlet],
  standalone: true
})
export class AppComponent implements OnInit {
  // Track if we're in iframe/dialog mode (Angular 19+ signal)
  // Public so it can be used in the template
  isInIframeDialog = signal(false);

  // Track if we should hide the router outlet (only for dialog-only mode)
  // This is true ONLY when loaded with ?mode=iframe parameter
  shouldHideRouterOutlet = signal(false);

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private iframeSecurityService: IframeSecurityService,
    private ferrerAuth: FerrerAuthService,
    private translocoService: TranslocoService,
    private dialogService: DialogService,
    private authCheckService: AuthCheckService,
    private sessionService: SessionService,
    private navigationFlow: NavigationFlowService
  ) {
    // Expose global methods in the constructor
    this.exposeGlobalMethods();

    // Add/remove iframe-mode class on body when isInIframeDialog changes
    effect(() => {
      const isIframe = this.isInIframeDialog();
      if (isIframe) {
        document.body.classList.add('iframe-mode');
        console.log('[APP] 🎨 Added iframe-mode class to body');
      } else {
        document.body.classList.remove('iframe-mode');
        console.log('[APP] 🎨 Removed iframe-mode class from body');
      }
    });
  }
  userUrl: string;
  private initializationComplete = false;
  private _sessionRequestInProgress = false;
  private currentAuthRequest: Subscription | null = null;

  private originalUrl: string = '';
  ngOnInit(): void {
    this.initializationComplete = false;

    // Debug: Send diagnostic info to parent
    const isInIframe = window.self !== window.top;
    console.log('[APP] 🔍 Diagnostic Info:', {
      isInIframe: isInIframe,
      'window.self': window.self,
      'window.top': window.top,
      'location.href': window.location.href
    });

    // Send to parent for debugging
    try {
      window.parent.postMessage({
        type: 'diagnostic',
        isInIframe: isInIframe,
        location: window.location.href
      }, '*');
    } catch (e) {
      console.error('[APP] Could not send diagnostic to parent:', e);
    }

    // Handle authentication issues early, but respect exempt routes
    let originalFullUrl = '';
    try {
      originalFullUrl = sessionStorage.getItem('originalFullUrl') || '';
    } catch (error) {
      console.warn('sessionStorage not accessible:', error);
      originalFullUrl = window.location.href;
    }
    //const originalParamsStr = sessionStorage.getItem('originalParams');
    //const originalQueryParamsStr = sessionStorage.getItem('originalQueryParams');

    this.originalUrl = originalFullUrl;

    // Check for iframe dialog-only mode via query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const iframeMode = urlParams.get('mode');
    if (iframeMode === 'iframe') {
      console.log('[APP] 🎯 Detected mode=iframe parameter - enabling dialog-only mode');
      this.isInIframeDialog.set(true);
      this.shouldHideRouterOutlet.set(true);

      // Don't process any routes automatically, wait for postMessage commands
    }

    console.log('[APP] 🔍 Iframe detection:', {
      'window.self !== window.top': window.self !== window.top,
      'window.self': window.self,
      'window.top': window.top,
      'mode param': iframeMode
    });

    if (window.self !== window.top || iframeMode === 'iframe') {
      console.log('[APP] 🖼️ Running in iframe mode');

      // If mode=iframe, always setup messaging regardless of iframe detection
      if (iframeMode === 'iframe') {
        console.log('[APP] 🎯 Mode=iframe detected, forcing iframe messaging setup');
        this.setupIframeMessaging();
        return; // Skip security checks for explicit iframe mode
      }

      const isAllowed = this.iframeSecurityService.checkOriginSecurity();
      console.log('[APP] 🔐 Origin security check result:', isAllowed);

      if (!isAllowed) {
        console.warn('[APP] ⚠️ Origin not allowed, checking if localhost for development...');

        // For development with localhost, allow but log warning
        const parentOrigin = window.location.ancestorOrigins?.[0] || document.referrer;
        const isLocalhost = parentOrigin.includes('localhost') || parentOrigin.includes('127.0.0.1');

        if (!isLocalhost) {
          console.error('[APP] ❌ Unauthorized embedding from:', parentOrigin);
          this.iframeSecurityService.handleUnauthorizedEmbedding();
          return;
        }

        console.log('[APP] ✅ Localhost detected, allowing for development');
      }

      // Always set up message listeners if in iframe (security check is done inside listener)
      this.setupIframeMessaging();
    } else {
      console.log('[APP] 🖥️ Running in standalone mode (not in iframe)');
    }

    // Listen for route changes to detect auth/login/:authId
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe((event: NavigationStart) => {
      // Match any URL that follows the pattern /auth/login/{uuid}

      if (this.initializationComplete) {
        return;
      }
      this.initializationComplete = true;
      const isActivationUrl = this.isActivationUrl(this.originalUrl);
      this.userUrl = event.url;
      localStorage.removeItem('userMail');
      // Modified regex to accept any alphanumeric authId (not just hex) and query params
      const authUrlPattern = /\/auth\/login\/([a-z0-9-]+)(\?.*)?/i;
      // Use event.url (path only) instead of originalUrl (full URL with protocol)
      const match = event.url.toLowerCase().match(authUrlPattern);
      // Skip if dialog already open OR in iframe mode (iframe has different initialization flow)
      if (this.dialog.openDialogs.length > 0 || this.isInIframeDialog()) {
        return;
      }
      if (match) {
        // Extract authId from URL path (group 1 in regex)
        if (this.shouldRedirectBasedOnCountry()) {
          this.handleCountryBasedRedirection(this.userUrl);
          return;
        }

        const authId = match[1];

        // Parse query parameters via helper
        const [hasQueryParams, queryParams] = this.ClearQueryParamsUrl(this.originalUrl);
        let params: any = {};

        if (hasQueryParams) {
          this.gettingSessiondata(authId);

          // Get the source parameter
          const source = queryParams.get('source');
          if (source) {
            params.source = source;

            // Try to decode base64 source
            try {
              params.decodedSource = JSON.parse(atob(source));
            } catch (e) {
              console.error('Error decoding source parameter:', e);
            }
            const sessionId = queryParams.get('sessionId');
            if (sessionId) {
              params.sessionId = sessionId;
            }
            const callback = queryParams.get('callback');
            if (callback) {
              params.callback = callback;
            }
            const product = queryParams.get('product');
            if (product) {
              params.product = product;
            }
          }
        }

        // Store these values
        this.storeAuthParameters(authId, params);

        // Decidir cómo abrir el diálogo basándose en el contexto
        this.decideDialogOpeningMode('login', authId, params);
      }


      const rememberRedirectUrlPattern = /\/auth\/rememberPassword\/([a-f0-9-]+)/i;
      const rememberRedirectMatch = this.originalUrl.toLowerCase().match(rememberRedirectUrlPattern);
      if (this.dialog.openDialogs.length > 0 || this.isInIframeDialog()) {
        return;
      }
      if (rememberRedirectMatch) {
        // Extract authId from URL path (group 1 in regex)
        if (this.shouldRedirectBasedOnCountry()) {
          this.handleCountryBasedRedirection(this.userUrl);
          return;
        }
        const authId = rememberRedirectMatch[1];
        // Parse query parameters via helper
        const [hasQueryParams, queryParams] = this.ClearQueryParamsUrl(this.originalUrl);
        let params: any = {};

        if (hasQueryParams) {
          this.gettingSessiondata(authId);
          // Get the source parameter
          const source = queryParams.get('source');
          if (source) {
            params.source = source;

            // Try to decode base64 source
            try {
              params.decodedSource = JSON.parse(atob(source));
            } catch (e) {
              console.error('Error decoding source parameter:', e);
            }
          }
          const sessionId = queryParams.get('sessionId');
          if (sessionId) {
            params.sessionId = sessionId;
          }
          const callback = queryParams.get('callback');
          if (callback) {
            params.callback = callback;
          }
          const product = queryParams.get('product');
          if (product) {
            params.product = product;
          }
        }

        // Store these values
        this.storeAuthParameters(authId, params);

        // Decidir cómo abrir el diálogo basándose en el contexto
        this.decideDialogOpeningMode('otp', authId, params);
      }

      const signupRedirectUrlPattern = /\/auth\/signup\/([a-z0-9-]+)(\?.*)?/i;
      const signupRedirectMatch = this.originalUrl.toLowerCase().match(signupRedirectUrlPattern);
      if (this.dialog.openDialogs.length > 0 || this.isInIframeDialog()) {
        return;
      }
      if (signupRedirectMatch) {
        // Extract authId from URL path (group 1 in regex)
        if (this.shouldRedirectBasedOnCountry()) {
          this.handleCountryBasedRedirection(this.userUrl);
          return;
        }
        const authId = signupRedirectMatch[1];
        // Parse query parameters via helper
        const [hasQueryParams, queryParams] = this.ClearQueryParamsUrl(this.originalUrl);
        let params: any = {};

        if (hasQueryParams) {
          this.gettingSessiondata(authId);
          // Get the source parameter
          const source = queryParams.get('source');
          if (source) {
            params.source = source;

            // Try to decode base64 source
            try {
              params.decodedSource = JSON.parse(atob(source));
            } catch (e) {
              console.error('Error decoding source parameter:', e);
            }
          }
          const sessionId = queryParams.get('sessionId');
          if (sessionId) {
            params.sessionId = sessionId;
          }
          const callback = queryParams.get('callback');
          if (callback) {
            params.callback = callback;
          }
          const product = queryParams.get('product');
          if (product) {
            params.product = product;
          }
        }

        // Store these values
        this.storeAuthParameters(authId, params);

        // Decidir cómo abrir el diálogo basándose en el contexto
        this.decideDialogOpeningMode('register', authId, params);
      }

      const rememberUrlPattern = /\/auth\/remember\/([a-f0-9-]+)/i;
      const rememberMatch = this.originalUrl.toLowerCase().match(rememberUrlPattern);

      if (rememberMatch) {
        // Extract authId from URL path (group 1 in regex)
        if (this.shouldRedirectBasedOnCountry()) {
          this.handleCountryBasedRedirection(this.userUrl);
          return;
        }
        const authId = rememberMatch[1];

        // Parse query parameters
        const [hasQueryParams, queryParams] = this.ClearQueryParamsUrl(this.originalUrl);
        let params: any = {};
        let email = null;

        if (hasQueryParams) {

          // Get the email parameter
          email = queryParams.get('email');
          if (email) {
            localStorage.setItem('userMail', email);
          }
          // Get other parameters as needed


          // Get any other specific parameters
          //const apiAuthId = queryParams.get('Api-Auth-Id');
          const apiSourceInfo = queryParams.get('Api-Source-Info');

          if (authId) {
            localStorage.setItem('externalAuthId', authId);
            this.gettingSessiondata(authId);
          }

          if (apiSourceInfo) {
            localStorage.setItem('externalSource', apiSourceInfo);
            params.source = apiSourceInfo;
          }
          const sessionId = queryParams.get('Api-Session-Id');
          if (sessionId) {
            localStorage.setItem('sessionId', sessionId);
          }
          const callback = queryParams.get('Api-Callback-Url');
          if (callback) {
            params.callback = callback;
            localStorage.setItem('callback', callback);
          }
          const product = queryParams.get('Api-Product-Flag');
          if (product) {
            params.product = product;
            localStorage.setItem('product', product);
          }
          const apiVersion = queryParams.get('Api-Version');
          if (apiVersion) {
            params.apiVersion = apiVersion;
            localStorage.setItem('apiVersion', apiVersion);
          }

          this.storeAuthParameters(authId, params);
        }
        // Process the remember password flow
        this.processRememberPassword(authId, email, params);
      }
      const activationUrlPattern = /\/auth\/activate\/([a-f0-9-]+)/i;
      const activationMatch = this.originalUrl.toLowerCase().match(activationUrlPattern);
      if (activationMatch) {
        // Extract UUID from URL path
        if (this.shouldRedirectBasedOnCountry()) {
          this.handleCountryBasedRedirection(this.originalUrl);
          return;
        }
        const uuid = activationMatch[1];
        // Parse query parameters via helper
        const [hasQueryParams, queryParams] = this.ClearQueryParamsUrl(this.originalUrl);
        let activationId = null;
        let apiAuthId = null;
        let apiSourceInfo = null;
        let apiCallbackUrl = null;
        let apiProductFlag = null;
        let apiSessionId = null;
        let apiVersion = null;
        if (hasQueryParams) {
          activationId = queryParams.get('activationId');
          apiAuthId = queryParams.get('Api-Auth-Id');
          apiSourceInfo = queryParams.get('Api-Source-Info');
          apiCallbackUrl = queryParams.get('Api-Callback-Url');
          apiProductFlag = queryParams.get('Api-Product-Flag');
          apiSessionId = queryParams.get('Api-Session-Id');
          apiVersion = queryParams.get('Api-Version');

          if (apiAuthId) {
            localStorage.setItem('externalAuthId', apiAuthId);
            this.gettingSessiondata(apiAuthId);
          }

          if (apiSourceInfo) {
            localStorage.setItem('externalSource', apiSourceInfo);
          }
          if (apiCallbackUrl) {
            localStorage.setItem('callback', apiCallbackUrl);
          }
          if (apiProductFlag) {
            localStorage.setItem('product', apiProductFlag);
          }
          if (apiSessionId) {
            localStorage.setItem('sessionId', apiSessionId);
          }
          if (apiVersion) {
            localStorage.setItem('apiVersion', apiVersion);
          }

          this.storeAuthParameters(apiAuthId, apiSourceInfo);
        }

        // If both parameters are present, process activation
        if (uuid && activationId) {
          this.processAccountActivation(uuid, activationId);
          if (window.self !== window.top || (apiVersion && apiVersion === '20')) {

            var callbackUrl = localStorage.getItem('callback');
            if (callbackUrl) {
              window.location.href = callbackUrl + '?Api-Auth-Id=' + apiAuthId + '&Api-Source-Info=' + apiSourceInfo + (apiCallbackUrl ? '&Api-Callback-Url=' + apiCallbackUrl : '') + (apiProductFlag ? '&Api-Product-Flag=' + apiProductFlag : '') + (apiSessionId ? '&Api-Session-Id=' + apiSessionId : '') + '&Api-Version=' + apiVersion;
              return;
            }

            this.isInIframeDialog.set(true);
            this.openLoginDialog(true); // true = use external auth params
          } else {
            // If not in iframe, redirect to home with flags to show login
            this.router.navigate(['/home'], {
              queryParams: { showLogin: 'true' }
            });
          }

        } else {
          console.error('Missing activation parameters');
        }
      }

      //campaign detect
      const campaignUrlPattern = /\/campaign\/([a-f0-9-]+)/i;
      const campaignMatch = event.url.toLowerCase().match(campaignUrlPattern);

      if (campaignMatch) {
        // Extract authId from URL path (group 1 in regex)
        if (this.shouldRedirectBasedOnCountry()) {
          this.handleCountryBasedRedirection(this.userUrl);
          return;
        }
        const idLanding = campaignMatch[1];

        // Parse query parameters via helper
        const [hasQueryParams, queryParams] = this.ClearQueryParamsUrl(this.originalUrl);
        let code = null;
        let campaignName = null;
        let queryString = '';
        if (hasQueryParams) {
          // Get the email parameter
          code = queryParams.get('code');
          campaignName = queryParams.get('campaignName');
          queryString = queryParams.toString();
        }

        this.redirectToCampaignEnv(idLanding, code, campaignName, queryString);
      }

      if (isActivationUrl) {
        return; // Skip the cookie validation below
      }
      const hasAllRequiredCookies = this.authCheckService.checkAuthRequirements();

      if (!hasAllRequiredCookies) {
        this.authCheckService.handleUnauthorized();
        return; // Important: stop initialization if unauthorized
      }
    });
  }

  private handleCountryBasedRedirection(userUrl: string): void {
    // Define different redirect URLs based on country
    const redirectMap = {
      'AT': 'https://hcp-at.ferrer.com',
      // Add more countries as needed
    };

    const sessionData = localStorage.getItem('ferrerSessionData');
    if (!sessionData) {
      console.error('No session data found, cannot perform country-based redirection');
      return;
    }

    try {
      const session = JSON.parse(sessionData);
      const countryCode = session?.country || session?.region;

      // Check if countryCode exists and has a mapping
      if (!countryCode || !redirectMap[countryCode]) {
        console.warn(`No redirect URL defined for country code: ${countryCode}`);
        return;
      }

      const baseUrl = redirectMap[countryCode];

      // Make sure the base URL ends with a slash if parameters exist
      const formattedBaseUrl = userUrl && !baseUrl.endsWith('/') ? `${baseUrl}/` : baseUrl;

      // Get the path after domain from the userUrl if it exists
      const pathAfterDomain = userUrl ? this.getPathAfterDomain(userUrl) : '';

      // Combine the base URL with parameters
      const redirectUrl = pathAfterDomain ? `${formattedBaseUrl}${pathAfterDomain}` : formattedBaseUrl;


      // Store a flag to prevent loops
      localStorage.setItem('countryRedirectPending', 'true');

      // Redirect to the country-specific URL
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('Error during country-based redirection:', error);
    }

  }

  private getPathAfterDomain(url: string): string {
    try {
      // Parse the URL using URL API
      const urlObj = new URL(url);

      // Combine pathname and search (query parameters)
      // This gives us everything after the domain including the leading slash
      const fullPath = urlObj.pathname + urlObj.search;

      // Remove the leading slash if it exists
      return fullPath.startsWith('/') ? fullPath.substring(1) : fullPath;
    } catch (error) {
      console.error('Error parsing URL:', error);
      return '';
    }

    // Encapsulates query param parsing from a URL and returns a tuple
  }
  // [hasQueryParams, queryParams]
  private ClearQueryParamsUrl(originalUrl: string): [boolean, URLSearchParams] {
    try {
      const urlObj = new URL(originalUrl);
      const qp = urlObj.searchParams;
      return [qp.toString().length > 0, qp];
    } catch {
      return [false, new URLSearchParams()];
    }
  }

  private shouldRedirectBasedOnCountry(): boolean {
    // Check if we've already handled a redirect to prevent loops
    if (localStorage.getItem('countryRedirectPending') === 'true') {
      // Clear the flag and skip redirect
      localStorage.removeItem('countryRedirectPending');
      return false;
    }

    const redirectCountries = ['AT'];
    const sessionData = JSON.parse(localStorage.getItem('ferrerSessionData'));
    if (!sessionData) {
      return false;
    }
    try {
      const session = JSON.parse(sessionData);
      return session && session.country && redirectCountries.includes(session.country);
    } catch (error) {
      return false;
    }
  }
  private isActivationUrl(url: string): boolean {
    const activationPatterns = [
      /\/auth\/activate\/([a-f0-9-]+)/i,
      /\/auth\/verification\//i,
      /\/auth\/remember\/([a-f0-9-]+)/i,
      /\/auth\/rememberPassword\/([a-f0-9-]+)/i,
      /\/auth\/signup\/([a-z0-9-]+)(\?.*)?/i,
      /\/campaign\/([a-f0-9-]+)/i
    ];
    const lowerCaseUrl = url.toLowerCase();
    return activationPatterns.some(pattern => pattern.test(lowerCaseUrl));
  }
  /**
   * Store external auth parameters
   */
  private storeAuthParameters(authId: string, params: any): void {
    // Store parameters in localStorage
    localStorage.setItem('externalAuthId', authId);
    //if (!this.initializationComplete){
    this.gettingSessiondata(authId);
    //}    
    if (params.source) {

      localStorage.setItem('externalSource', params.source);
    }
    if (params.sessionId) {
      localStorage.setItem('sessionId', params.sessionId);
    }
    if (params.callback) {
      localStorage.setItem('callback', params.callback);
    }
    if (params.product) {
      localStorage.setItem('product', params.product);
    }
    if (params.decodedSource) {
      localStorage.setItem('decodedSource', JSON.stringify(params.decodedSource));
    }
    if (params.apiVersion) {
      localStorage.setItem('apiVersion', params.apiVersion);
    }
  }

  /**
   * Get stored external auth parameters
   */
  private getAuthParameters(): any {
    const authId = localStorage.getItem('externalAuthId');
    const source = localStorage.getItem('externalSource');
    let decodedSource = null;

    try {
      const decodedSourceStr = localStorage.getItem('decodedSource');
      if (decodedSourceStr) {
        decodedSource = JSON.parse(decodedSourceStr);
      }
    } catch (e) {
      console.error('Error parsing decoded source:', e);
    }

    if (authId) {
      return {
        authId,
        source,
        decodedSource
      };
    }

    return null;
  }

  /**
   * Clear stored auth parameters
   */
  private clearAuthParameters(): void {
    // localStorage.removeItem('externalAuthId');
    // localStorage.removeItem('externalSource');
    // localStorage.removeItem('decodedSource');
  }

  /**
   * Decide cómo abrir el diálogo basándose en:
   * 1. Si noAutoDialog=true está en la URL (espera postMessage del padre)
   * 2. Si está embebido en iframe (window.self !== window.top)
   * 3. Si el digital asset (authId) está configurado como v2.0 en sessionData
   * 
   * Esta función espera a que se cargue la sessionData antes de decidir
   */
  private decideDialogOpeningMode(
    dialogType: 'login' | 'register' | 'otp',
    authId: string,
    params: any
  ): void {
    // FIRST: Check if noAutoDialog=true (takes precedence over everything)
    // This means: Load background but wait for postMessage from parent
    let noAutoDialog = false;

    // Check URL first (URL params have priority over sessionStorage)
    const urlParams = new URLSearchParams(window.location.search);
    const noAutoDialogInUrl = urlParams.get('noAutoDialog') === 'true';

    if (noAutoDialogInUrl) {
      noAutoDialog = true;
    } else {
      // URL doesn't have the param, check sessionStorage (for page reloads only)
      try {
        const noAutoDialogFromStorage = sessionStorage.getItem('originalNoAutoDialog');
        noAutoDialog = noAutoDialogFromStorage === 'true';

        // If URL explicitly doesn't have the param and we're checking, clear sessionStorage
        // This prevents old values from persisting across different navigation contexts
        if (!noAutoDialogInUrl && noAutoDialogFromStorage) {
          console.log('[APP] Clearing old noAutoDialog from sessionStorage (URL does not have this param)');
          sessionStorage.removeItem('originalNoAutoDialog');
          noAutoDialog = false;
        }
      } catch (e) {
        console.warn('[APP] Could not read from sessionStorage:', e);
      }
    }

    if (noAutoDialog) {
      console.log('[APP] noAutoDialog=true detected - will wait for postMessage commands');

      // Only set iframe dialog mode if we're actually in an iframe
      if (window.self !== window.top) {
        console.log('[APP] In iframe with noAutoDialog - enabling dialog-only mode');
        this.isInIframeDialog.set(true);
      } else {
        console.log('[APP] In standalone with noAutoDialog - will show page content');
      }

      // Do NOT call openDialogByType() - wait for postMessage from parent or manual action
      return;
    }

    // SECOND: Si ya está embebido en iframe (sin noAutoDialog), abrir directamente
    if (window.self !== window.top) {
      this.isInIframeDialog.set(true);
      this.openDialogByType(dialogType, true);
      return;
    }

    // Si no está en iframe, verificar si el digital asset es v2.0
    // Esperar un momento a que se cargue la sessionData
    setTimeout(() => {
      const isV2 = this.isApiVersion2();

      if (isV2) {
        // Es v2.0: abrir como diálogo (simular modo iframe)
        this.isInIframeDialog.set(true);
        this.openDialogByType(dialogType, true);
      } else {
        // Es v1.5: redirigir a /home con query params (modo standalone)
        this.redirectToHomeWithDialog(dialogType);
      }
    }, 100); // Pequeño delay para permitir que se cargue sessionData
  }

  /**
   * Abre el diálogo según el tipo
   */
  private openDialogByType(
    dialogType: 'login' | 'register' | 'otp',
    useExternalAuth: boolean
  ): void {
    switch (dialogType) {
      case 'login':
        this.openLoginDialog(useExternalAuth);
        break;
      case 'register':
        this.openRegisterDialog(useExternalAuth);
        break;
      case 'otp':
        const email = localStorage.getItem('userMail') || '';
        const authId = localStorage.getItem('externalAuthId') || '';
        this.openRememberPasswordDialog(email, authId);
        break;
    }
  }

  /**
   * Redirige a /home con los query params apropiados
   */
  private redirectToHomeWithDialog(dialogType: 'login' | 'register' | 'otp'): void {
    const queryParams: any = {};

    switch (dialogType) {
      case 'login':
        queryParams.showLogin = 'true';
        break;
      case 'register':
        queryParams.showRegister = 'true';
        break;
      case 'otp':
        queryParams.showRememberDialog = 'true';
        const email = localStorage.getItem('userMail');
        const authId = localStorage.getItem('externalAuthId');
        if (email) queryParams.email = email;
        if (authId) queryParams.authId = authId;
        break;
    }

    this.router.navigate(['/home'], { queryParams });
  }

  /**
   * Verifica si el digital asset está configurado como v2.0
   */
  private isApiVersion2(): boolean {
    try {
      const sessionData = localStorage.getItem('ferrerSessionData');
      if (!sessionData) {
        return false;
      }
      const parsedData = JSON.parse(sessionData);
      const isV2 = parsedData?.apiVersion === '2.0';
      console.log('[APP] Checking API version:', isV2 ? '2.0' : '1.5');
      return isV2;
    } catch (error) {
      console.error('[APP] Error checking API version:', error);
      return false;
    }
  }

  private setupIframeMessaging(): void {
    // Get the verified parent origin
    const parentOrigin = this.iframeSecurityService.getParentOrigin();
    console.log('[APP] 🔐 Setting up iframe messaging with parent origin:', parentOrigin);

    // Send ready message to parent IMMEDIATELY after setting up listener
    const sendReadyMessage = () => {
      if (window.parent && window.parent !== window) {
        const readyMessage = {
          type: 'iframe.listenerReady',
          timestamp: Date.now()
        };
        window.parent.postMessage(readyMessage, '*');
        console.log('[APP] 📤 Sent iframe.listenerReady to parent:', readyMessage);
      }
    };

    // Send ready message immediately
    sendReadyMessage();

    // Also send it after a small delay in case parent wasn't ready
    setTimeout(() => {
      sendReadyMessage();
    }, 100);

    // Listen for messages from parent iframe
    window.addEventListener('message', (event) => {
      // Log all messages for debugging
      console.log('[APP] 📨 Received postMessage:', {
        data: event.data,
        origin: event.origin,
        parentOrigin: parentOrigin
      });

      // Only process messages from verified origin OR allow localhost for development
      const isAllowedOrigin = !parentOrigin ||
        event.origin === parentOrigin ||
        event.origin.includes('localhost') ||
        event.origin.includes('127.0.0.1');

      if (!isAllowedOrigin) {
        console.warn('[APP] ⚠️ Ignoring message from unverified origin:', event.origin);
        return;
      }

      // Handle authId setup via postMessage (for v2.0 iframe mode)
      if (typeof event.data === 'object' && event.data?.type === 'setAuthId') {
        const { authId, source, apiVersion, sessionId, callbackUrl, productFlag } = event.data;
        console.log('[APP] 🔑 Received setAuthId via postMessage:', event.data);

        if (authId) {
          localStorage.setItem('externalAuthId', authId);
          console.log('[APP] ✅ Stored authId in localStorage:', authId);
        }

        if (source) {
          localStorage.setItem('externalSource', source);
          console.log('[APP] ✅ Stored externalSource in localStorage:', source);
        } else {
          console.warn('[APP] ⚠️ No source provided in setAuthId message, using default');
          localStorage.setItem('externalSource', 'iframe-postMessage');
          console.log('[APP] ✅ Stored default externalSource in localStorage: iframe-postMessage');
        }

        if (apiVersion) {
          localStorage.setItem('apiVersion', apiVersion);
          console.log('[APP] ✅ Stored apiVersion in localStorage:', apiVersion);
        }

        if (sessionId) {
          localStorage.setItem('sessionId', sessionId);
          console.log('[APP] ✅ Stored sessionId in localStorage:', sessionId);
        }

        if (callbackUrl) {
          localStorage.setItem('callback', callbackUrl);
          console.log('[APP] ✅ Stored callback in localStorage:', callbackUrl);
        }

        if (productFlag) {
          localStorage.setItem('product', productFlag);
          console.log('[APP] ✅ Stored product in localStorage:', productFlag);
        }

        // Verify values are now in localStorage
        console.log('[APP] 🔍 Verification - localStorage after setAuthId:', {
          externalAuthId: localStorage.getItem('externalAuthId'),
          externalSource: localStorage.getItem('externalSource'),
          apiVersion: localStorage.getItem('apiVersion'),
          sessionId: localStorage.getItem('sessionId'),
          callback: localStorage.getItem('callback'),
          product: localStorage.getItem('product'),
          allKeys: Object.keys(localStorage)
        });

        // Call gettingSessiondata to fetch session info
        if (authId) {
          console.log('[APP] 📞 Calling gettingSessiondata with authId:', authId);
          this.gettingSessiondata(authId);
        }

        return;
      }

      // Standard login dialog opening
      if (event.data === 'openLoginDialog' || (typeof event.data === 'object' && event.data?.type === 'openLoginDialog')) {
        // Ignore if wrongUuid is showing
        if (this.authCheckService.isShowingWrongUuidDialog()) {
          console.log('[APP] ⛔ Ignoring openLoginDialog - wrongUuid is showing');
          return;
        }

        console.log('[APP] 🔑 Opening login dialog via postMessage');
        this.isInIframeDialog.set(true);

        // ✅ CHECK IF WE HAVE EXTERNAL AUTH PARAMS IN LOCALSTORAGE
        const hasExternalAuth = !!localStorage.getItem('externalAuthId');
        console.log('[APP] 🔍 Has external auth in localStorage?', hasExternalAuth);

        // Ensure externalSource is set for auth check
        if (!localStorage.getItem('externalSource')) {
          localStorage.setItem('externalSource', 'iframe-postMessage');
          console.log('[APP] ✅ Set default externalSource for iframe postMessage');
        }

        // ✅ USE TRUE IF WE HAVE EXTERNAL AUTH PARAMS (from URL or postMessage)
        this.openLoginDialog(hasExternalAuth);
      }

      // Standard register dialog opening
      if (event.data === 'openRegisterDialog' || (typeof event.data === 'object' && event.data?.type === 'openRegisterDialog')) {
        // Ignore if wrongUuid is showing
        if (this.authCheckService.isShowingWrongUuidDialog()) {
          console.log('[APP] ⛔ Ignoring openRegisterDialog - wrongUuid is showing');
          return;
        }

        console.log('[APP] 📝 Opening register dialog via postMessage');
        this.isInIframeDialog.set(true);

        // ✅ CHECK IF WE HAVE EXTERNAL AUTH PARAMS
        const hasExternalAuth = !!localStorage.getItem('externalAuthId');
        console.log('[APP] 🔍 Has external auth in localStorage?', hasExternalAuth);

        // Ensure externalSource is set for auth check
        if (!localStorage.getItem('externalSource')) {
          localStorage.setItem('externalSource', 'iframe-postMessage');
          console.log('[APP] ✅ Set default externalSource for iframe postMessage');
        }

        // ✅ USE TRUE IF WE HAVE EXTERNAL AUTH PARAMS
        this.openRegisterDialog(hasExternalAuth);
      }

      // External auth login dialog - handle structured message
      if (typeof event.data === 'object' && event.data?.type === 'openExternalAuthLogin') {
        const { authId, source } = event.data;
        if (authId) {
          this.storeAuthParameters(authId, { source });
        }

        this.isInIframeDialog.set(true);
        this.openLoginDialog(true);
      }

      // Keep overlay open
      if (event.data === 'keepOverlayOpen') {
        this.isInIframeDialog.set(true);
      }

      // Listen for explicit close command
      if (event.data === 'closeOverlay') {
        this.isInIframeDialog.set(false);
        this.notifyParentClosed();
      }
    });
  }

  /**
   * Expose methods to the global window object for external access
   */
  private exposeGlobalMethods(): void {
    // Method to open login dialog
    (window as any).openAngularLoginDialog = () => {
      this.isInIframeDialog.set(true);
      this.openLoginDialog(false);
    };

    // Method to open login dialog with external auth parameters
    (window as any).openExternalAuthLoginDialog = (authId: string, source: string) => {
      if (authId) {
        this.storeAuthParameters(authId, { source });
      }

      this.isInIframeDialog.set(true);
      this.openLoginDialog(true);
    };

    // Method to transition from email verification to login
    (window as any).transitionToLogin = async () => {
      console.log('[GLOBAL] transitionToLogin called, closing all dialogs and opening login');
      this.navigationFlow.closeAllDialogs();

      // Wait a bit for dialog animations to complete
      await new Promise(resolve => setTimeout(resolve, 300));

      // Open login dialog after all dialogs are closed
      console.log('[GLOBAL] Opening login after closing all dialogs');
      this.openLoginDialog(false);
    };

    // Method to close all dialogs and notify parent (for X button in iframe mode)
    (window as any).closeAllDialogs = () => {
      console.log('[GLOBAL] closeAllDialogs called, closing all dialogs and iframe');
      this.navigationFlow.closeAllDialogs();
      this.isInIframeDialog.set(false);
      this.notifyParentClosed();
    };

    // Method to check if dialog is active
    (window as any).isDialogActive = () => {
      return this.navigationFlow.hasOpenDialogs() || this.isInIframeDialog();
    };

    // Method to close dialog from outside
    (window as any).closeAngularDialog = () => {
      this.isInIframeDialog.set(false);
      this.navigationFlow.closeAllDialogs();
      this.clearAuthParameters();
      this.notifyParentClosed();
    };
  }

  /**
   * Open the login dialog, optionally with external auth parameters
   */
  openLoginDialog(useExternalAuth: boolean = false): void {
    console.log('[LOGIN] openLoginDialog called, useExternalAuth:', useExternalAuth);

    // Prepare dialog data
    const dialogData: any = {};

    // If using external auth, include the parameters
    if (useExternalAuth) {
      const externalParams = this.getAuthParameters();
      if (externalParams) {
        dialogData.externalAuth = externalParams;
      }
    }

    // Use NavigationFlowService to open the dialog
    this.navigationFlow.openLoginDialog(dialogData).subscribe(result => {
      // If login was successful, clear the external auth parameters
      if (result && result.success) {
        this.clearAuthParameters();
      }

      // Handle action transitions (e.g., opening OTP dialog)
      if (result && result.action === 'openOtp') {
        // Open OTP dialog with the email
        this.openRememberPasswordDialog(result.email, '');
      }
    });
  }

  openRegisterDialog(useExternalAuth: boolean = false): void {
    // Prepare dialog data
    const dialogData: any = {};

    // If using external auth, include the parameters
    if (useExternalAuth) {
      const externalParams = this.getAuthParameters();
      if (externalParams) {
        dialogData.externalAuth = externalParams;
      }
    }

    // Use NavigationFlowService to open the dialog
    this.navigationFlow.openRegisterDialog(dialogData).subscribe(result => {
      console.log('[REGISTER] Dialog closed with result:', result);

      // If registration was successful, clear the external auth parameters
      if (result && result.success) {
        this.clearAuthParameters();
      }

      // Handle action transitions (e.g., opening login from email verification)
      if (result && result.action === 'openLogin') {
        console.log('[REGISTER] Opening login dialog from email verification');
        this.openLoginDialog(false);
      }
    });
  }

  private redirectToCampaignEnv(idLanding: string, code: string, campaignName: string, queryString: string): void {
    // Determine if we're in production based on hostname
    const isProd = window.location.hostname === 'app-cnnc-front-p1.azurewebsites.net' ||
      window.location.hostname === 'www.app-cnnc-front-p1.azurewebsites.net' ||
      window.location.hostname === 'connect.ferrer.com' ||
      window.location.hostname === 'www.connect.ferrer.com';

    // Set base URL according to environment
    //const baseUrl = isProd ? 'https://connect.ferrer.com' : 'https://connectdev.ferrer.com';
    //just use this url for testing

    const baseUrl = isProd ? 'https://app-cnnc-campaign-p1.azurewebsites.net' : 'https://ferrerconnectcampaign.azurewebsites.net';
    //const baseUrl = 'https://www.ferrerconnectcampaign.azurewebsites.net';
    // Construct the full redirect URL with all parameters
    let redirectUrl = `${baseUrl}/campaign/${idLanding}`;

    // Preserve original query parameters if they exist
    if (queryString) {
      redirectUrl += `?${queryString}`;
    }


    // Perform the redirection
    window.location.href = redirectUrl;
  }


  private gettingSessiondata(authId: string): void {

    if (this.currentAuthRequest) {
      this.currentAuthRequest.unsubscribe();
      this._sessionRequestInProgress = false;
    }
    this._sessionRequestInProgress = true;
    const externalAuthId = localStorage.getItem('externalAuthId');
    localStorage.setItem('externalAuthId', authId);
    if (externalAuthId && externalAuthId !== authId) {
      //localStorage.clear();
    }
    this.currentAuthRequest = this.ferrerAuth.getSessiondata(authId).subscribe({
      next: (response) => {
        this._sessionRequestInProgress = false;
        const combinedSessionData = {
          ...response.customerAuthentication,
          countriesInRegion: response.countriesInRegion
        };
        const sessiondata = JSON.stringify(combinedSessionData);
        localStorage.setItem('ferrerSessionData', sessiondata);
        this.sessionService.loadSessionData();
        this.getCountryInfo(response.country);
        this.translocoService.setActiveLang(response?.customerAuthentication?.language ?? 'es');
      },
      error: (error) => {
        this._sessionRequestInProgress = false;
        console.error('Failed to send OTP code:', error);
        //this.router.navigate([]); 
        if (error.status === 401) {
          console.warn('Authentication failed (401 Unauthorized) for authId:', authId);
          setTimeout(() => {
            this.authCheckService.handleUnauthorizedWeb();
          }, 0);
        }

        // Set a flag to indicate authentication failure
        //localStorage.setItem('authenticationFailed', 'true');

        return;
        // We don't show errors to user in fire-and-forget scenarios
      }
    });
  }

  private getCountryInfo(region: string): void {
    this.ferrerAuth.getCountryCode(region).subscribe({
      next: (response) => {
        this.translocoService.setActiveLang(response?.language ?? 'es');
        localStorage.setItem('currentCountryInfo', JSON.stringify(response));
        // Continue with dialog opening only after successful OTP request

      },
      error: (error) => {
        console.error('Failed to send OTP code:', error);
        // We don't show errors to user in fire-and-forget scenarios
      }
    });
  }

  private processAccountActivation(uuid: string, activationId: string): void {
    this.ferrerAuth.sentVerifiedEmail(uuid, activationId).subscribe({
      next: (response) => {

        if (response) {
        }
        // Continue with dialog opening only after successful OTP request

      },
      error: (error) => {
        console.error('Failed to send OTP code:', error);
        // We don't show errors to user in fire-and-forget scenarios
      }
    });
  }
  /**
   * Helper to notify parent window that overlay should close
   */
  private notifyParentClosed(): void {
    const isIframe = window.self !== window.top;
    if (isIframe && window.parent && window.parent !== window) {
      console.log('[NOTIFY] Sending close messages to parent');
      console.log('[NOTIFY] Parent origin:', document.referrer);

      try {
        // Send multiple messages to ensure parent handles closure
        window.parent.postMessage('loginDialogClosed', '*');
        window.parent.postMessage('dialogClosed', '*');
        window.parent.postMessage('stopRetry', '*');
        window.parent.postMessage('closeDialog', '*'); // Additional fallback message
        window.parent.postMessage({
          type: 'loginDialogClosed',
          result: 'dismissed'
        }, '*');
        window.parent.postMessage({
          type: 'closeDialog',
          action: 'close'
        }, '*');
        console.log('[NOTIFY] All messages sent successfully');
      } catch (error) {
        console.error('[NOTIFY] Error sending messages to parent:', error);
      }
    }
  }
  private processRememberPassword(authId: string, email: string, params: any): void {
    // If we're in an iframe, open dialog immediately
    if (window.self !== window.top) {
      // Angular 19+: ngZone.run() no longer needed
      this.isInIframeDialog.set(true);
      this.openRememberPasswordDialog(email, authId); // Open dedicated dialog
    } else {
      // If not in iframe, redirect to home with flags to show ç dialog
      this.router.navigate(['/home'], {
        queryParams: {
          showRememberDialog: 'true',
          email: email,
          authId: authId
        }
      });
    }
  }

  /**
   * Open remember password dialog
   */
  openRememberPasswordDialog(email: string, authId: string): void {
    // Use NavigationFlowService to open the OTP dialog
    const dialogData: any = {
      email: email,
      authId: authId
    };

    this.navigationFlow.openOtpDialog(email).subscribe(result => {
      // Handle result if needed
    });
  }

  initializeApp() {
    //localStorage.clear();
  }
  // private isExemptRoute(url: string): boolean {
  // return url.includes('/login');
  // }
}